/**
 * Fountain import UI — paste or upload, parse, then a PRE-COMMIT summary
 * gate the writer must explicitly confirm before anything is saved (approved
 * 2026-07-14: "parse → show full summary → writer explicitly confirms →
 * then save"). Nothing is written until "Import" is clicked; closing this
 * panel at any point before that leaves the project untouched.
 *
 * Deliberately replaces the editor in its parent's render (rather than
 * overlaying it) while open — this destroys the live editor's autosave
 * scheduler for the duration of the import, so there is no live-editor
 * write racing the import's own save call through the same save client.
 */
import { useRef, useState } from 'react';
import { AlertTriangle, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fountainToElements, MAX_SOURCE_BYTES } from './fountain';

const ACCEPT = '.fountain,.txt';

/** Yields one macrotask so the "Parsing…" status can paint before the
 * (synchronous, potentially slow on a large file) parse blocks the thread —
 * approved: "don't over-engineer it; just don't let a big paste freeze the
 * editor." Real chunked/streaming parsing would be over-engineering at the
 * 5MB source cap this import allows. */
function yieldToPaint() {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

// ⚠ MATCHED PAIR: these keys must cover every counter freshReport() in
// fountain.js can emit — a report key with no label here crashes
// pluralizeCount on undefined. Pinned by a totality test in
// FountainImportPanel.test.jsx; change the two together.
export const REPORT_LABELS = {
  kept: {
    scene_heading: 'scene heading(s)',
    action: 'action(s)',
    character: 'character(s)',
    parenthetical: 'parenthetical(s)',
    dialogue: 'dialogue(s)',
    transition: 'transition(s)',
    shot: 'shot(s)',
  },
  converted: {
    headingsNormalized: 'scene heading(s) normalized to INT./EXT. form',
    forcedSceneHeadings: 'forced scene heading(s) (leading .)',
    forcedActions: 'forced action line(s) (leading !)',
    forcedCharacters: 'forced character cue(s) (leading @)',
    forcedTransitions: 'forced transition(s) (leading >)',
    dualDialoguePairs: 'dual-dialogue pair(s) — imported in sequence, simultaneous staging not preserved',
  },
  stripped: {
    sceneNumbers: 'scene number(s) (e.g. #12#)',
    sections: 'section marker(s) (# outlining)',
    synopses: 'synopsis line(s) (= outlining)',
    notes: 'note(s) ([[ ]])',
    notesUnterminated: 'unclosed [[ note marker(s) — left as plain text',
    boneyard: 'boneyard block(s) (/* */)',
    boneyardUnterminated: 'unclosed /* comment marker(s) — left as plain text',
    centered: 'centered line(s) (>text<) — centering not preserved',
    lyrics: 'lyric line(s) (~) — styling not preserved',
    pageBreaks: 'page break(s) (===)',
    emphasisMarkers: 'emphasis marker(s) (*, **, _)',
  },
};

function pluralizeCount(n, label) {
  return `${n} ${label.replace('(s)', n === 1 ? '' : 's')}`;
}

function SummaryList({ title, entries }) {
  if (entries.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {title}
      </p>
      <ul className="text-sm space-y-0.5">
        {entries.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

export default function FountainImportPanel({ hasExistingContent, onCancel, onConfirm }) {
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('input'); // input | parsing | summary | importing
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null); // { elements, report }
  // A 409 means our base rev is stale — every retry from this panel is
  // guaranteed to 409 again (and re-burn work server-side), so the only
  // honest affordance after one is the reload the message asks for.
  const [conflict, setConflict] = useState(false);
  const fileInputRef = useRef(null);
  // Guards double-click re-entry into handleConfirm: the disabled prop only
  // takes effect after a re-render, so two clicks in the same frame would
  // both run the confirm sequence.
  const confirmInFlightRef = useRef(false);
  // Parse must not run while a FileReader is still loading — it would parse
  // the PREVIOUS textarea content and present a summary of the wrong input.
  const [fileReading, setFileReading] = useState(false);

  const handleFile = (file) => {
    if (file.size > MAX_SOURCE_BYTES) {
      setError(`That file is ${file.size} bytes — the import limit is ${MAX_SOURCE_BYTES} bytes.`);
      return;
    }
    setFileReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setSource(String(reader.result ?? ''));
      setFileReading(false);
    };
    reader.onerror = () => {
      setError("Couldn't read that file.");
      setFileReading(false);
    };
    reader.readAsText(file); // handles UTF-8/BOM decoding
  };

  const handleParse = async () => {
    setError(null);
    setStatus('parsing');
    await yieldToPaint();
    try {
      const { elements, report } = fountainToElements(source);
      setParsed({ elements, report });
      setStatus('summary');
    } catch (err) {
      setStatus('input');
      setError(err.reason || err.message || 'Could not parse this file.');
    }
  };

  const handleConfirm = async () => {
    if (confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setStatus('importing');
    setError(null);
    try {
      await onConfirm(parsed.elements);
      // onConfirm reloads the page on success — nothing more to do here.
      // confirmInFlightRef deliberately stays true: the reload is coming.
    } catch (err) {
      confirmInFlightRef.current = false;
      setStatus('summary');
      if (err.status === 409) {
        setConflict(true);
        setError(
          'This project changed elsewhere while you were reviewing this import — reload the page and try again.',
        );
      } else {
        setError(err.message || 'Import failed — nothing was saved.');
      }
    }
  };

  if (status === 'summary' && parsed) {
    const { report } = parsed;
    const keptEntries = Object.entries(report.kept)
      .filter(([, n]) => n > 0)
      .map(([type, n]) => pluralizeCount(n, REPORT_LABELS.kept[type]));
    const convertedEntries = Object.entries(report.converted)
      .filter(([, n]) => n > 0)
      .map(([key, n]) => pluralizeCount(n, REPORT_LABELS.converted[key]));
    const strippedEntries = Object.entries(report.stripped)
      .filter(([, n]) => n > 0)
      .map(([key, n]) => pluralizeCount(n, REPORT_LABELS.stripped[key]));

    return (
      <div className="p-6 max-w-2xl mx-auto">
        <h3 className="font-mono text-lg font-semibold mb-1">Import summary</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Nothing has been saved yet — review this, then confirm.
        </p>

        {hasExistingContent && (
          <div className="mb-4 border border-destructive/40 bg-destructive/10 p-3 flex gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
            <p className="text-sm">
              This replaces your existing script. A snapshot is taken automatically —
              you can restore it from Version History if you change your mind.
            </p>
          </div>
        )}

        {report.titlePage.length > 0 && (
          <div className="mb-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              Title page found (not imported)
            </p>
            <ul className="text-sm space-y-0.5 text-muted-foreground">
              {report.titlePage.map((f, i) => (
                <li key={`${f.key}-${i}`}>
                  {f.key}: {f.value}
                </li>
              ))}
            </ul>
          </div>
        )}

        <SummaryList title={`Kept — ${report.elementCount} elements`} entries={keptEntries} />
        <SummaryList title="Converted" entries={convertedEntries} />
        <SummaryList title="Stripped — not part of the screenplay model" entries={strippedEntries} />

        {error && (
          <p className="font-mono text-xs text-destructive border border-destructive/30 bg-destructive/10 p-3 mb-4">
            {error}
          </p>
        )}

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            className="rounded-none font-mono uppercase text-xs tracking-widest"
            onClick={onCancel}
            disabled={status === 'importing'}
          >
            Cancel
          </Button>
          <Button
            className="rounded-none font-mono uppercase text-xs tracking-widest"
            onClick={handleConfirm}
            disabled={status === 'importing' || conflict}
          >
            {status === 'importing'
              ? 'Importing…'
              : hasExistingContent
                ? 'Import — Replace My Script'
                : 'Import'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h3 className="font-mono text-lg font-semibold mb-1">Import a Fountain screenplay</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Paste Fountain-formatted text, or upload a .fountain/.txt file.
        {hasExistingContent && ' This will replace your existing script — you\'ll see exactly what changes before anything is saved.'}
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = ''; // allow re-selecting the same file
        }}
      />
      <div className="flex gap-2 mb-3">
        <Button
          variant="outline"
          className="rounded-none font-mono uppercase text-xs tracking-widest"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-3 h-3 mr-1" /> Upload file
        </Button>
      </div>

      <textarea
        value={source}
        onChange={(e) => setSource(e.target.value)}
        aria-label="Fountain screenplay source"
        placeholder="Paste your Fountain-formatted screenplay here…"
        rows={12}
        className="w-full bg-background border border-input rounded-none font-mono text-xs p-3 text-foreground focus:ring-1 focus:ring-primary mb-1"
      />
      <p className="text-xs text-muted-foreground mb-4">
        Character names in scripts without letter case (e.g. Amharic) won't be detected
        automatically — write <code>@Name</code> at the start of the line to force it.
      </p>

      {error && (
        <p className="font-mono text-xs text-destructive border border-destructive/30 bg-destructive/10 p-3 mb-4">
          {error}
        </p>
      )}

      <div className="flex gap-2 justify-end">
        <Button
          variant="outline"
          className="rounded-none font-mono uppercase text-xs tracking-widest"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className="rounded-none font-mono uppercase text-xs tracking-widest"
          onClick={handleParse}
          disabled={!source.trim() || status === 'parsing' || fileReading}
        >
          {fileReading ? 'Reading file…' : status === 'parsing' ? 'Parsing…' : 'Parse'}
        </Button>
      </div>
    </div>
  );
}
